import React, { useState, useEffect, useCallback } from 'react';
import { Text } from '@cuemath/web-comps';
import { usePatchAgentDetails, invalidateAgents } from 'actions/support-agent';
import {
  GENERAL_TEAM_NAME,
  AGENT_ACTIONS,
  CACHE_KEYS,
  ERROR_AUTO_CLEAR_MS,
} from '../../constants';
import {
  FormField,
  FormTextInput,
  TeamAddAgentList,
  TeamAddAgentItem,
  TeamAgentAvatar,
  TeamAgentInfo,
  TeamAgentName,
  TeamAgentEmail,
  AgentOnlineDot,
  TeamEmpty,
} from './styled';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  IconBtn,
  ModalErrorMsg,
  SA_COLORS,
} from '../../styled-comps';
import Icon from '../icon';

const AddAgentToTeamModal = ({ team, agents, onClose }) => {
  const [query, setQuery] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const { patch: patchAgent, isProcessing: adding } = usePatchAgentDetails({
    onComplete: error => {
      if (error) {
        setSubmitError(typeof error === 'string' ? error : 'Failed to add agent');
      } else {
        invalidateAgents(CACHE_KEYS.AGENTS);
        handleClose();
      }
    },
  });

  const handleClose = useCallback(() => {
    setQuery('');
    setSubmitError(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!submitError) return undefined;
    const timer = setTimeout(() => setSubmitError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [submitError]);

  const handlePick = useCallback(
    agent => {
      if (adding || !team) return;
      const specificTeamIds = (agent.teams || [])
        .filter(t => t.name !== GENERAL_TEAM_NAME)
        .map(t => t.id);
      const nextTeamIds = Array.from(new Set([...specificTeamIds, team.id]));
      patchAgent(agent.user_id, {
        action: AGENT_ACTIONS.UPDATE_TEAMS,
        team_ids: nextTeamIds,
      });
    },
    [adding, patchAgent, team],
  );

  if (!team) return null;

  const q = query.trim().toLowerCase();
  const candidates = agents.filter(a => {
    const inTeam = a.teams?.some(t => t.id === team.id);
    if (inTeam) return false;
    if (!q) return true;
    return (
      (a.name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1Medium">Add Agent to {team.name}</Text>
          <IconBtn onClick={handleClose}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>
        <ModalBody>
          {submitError && <ModalErrorMsg>{submitError}</ModalErrorMsg>}
          <FormField>
            <FormTextInput
              autoFocus
              type="text"
              placeholder="Search agents by name or email..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </FormField>

          {candidates.length === 0 ? (
            <TeamEmpty>No matching agents</TeamEmpty>
          ) : (
            <TeamAddAgentList>
              {candidates.map((agent, idx) => {
                const highlighted = idx === 0 && q.length > 0;
                return (
                  <TeamAddAgentItem
                    key={agent.user_id}
                    $selected={highlighted}
                    disabled={adding}
                    onClick={() => handlePick(agent)}
                  >
                    <TeamAgentAvatar>
                      {(agent.name || '--').slice(0, 2).toUpperCase()}
                      <AgentOnlineDot $online={agent.is_online} />
                    </TeamAgentAvatar>
                    <TeamAgentInfo>
                      <TeamAgentName>{agent.name}</TeamAgentName>
                      {agent.email && <TeamAgentEmail>{agent.email}</TeamAgentEmail>}
                    </TeamAgentInfo>
                    {highlighted ? (
                      <Icon name="check" size={16} color={SA_COLORS.SUCCESS_700} />
                    ) : (
                      <Icon name="plus" size={16} />
                    )}
                  </TeamAddAgentItem>
                );
              })}
            </TeamAddAgentList>
          )}
        </ModalBody>
      </ModalCard>
    </ModalOverlay>
  );
};

export default AddAgentToTeamModal;
